import React, { useState, useCallback, useEffect, useRef } from "react";
import { Box, BoxStatus, GraphicFilters } from "codekit";
import { useForm, useWatch } from "react-hook-form";
import {
  useLazyGetEntregauDataQuery,
  useLazyGetEntregauDetailQuery,
  useGetEntregauGerenciasQuery,
  useLazyGetEntregauGruposQuery,
} from "../../../../libs/redux/slices/Graficsx/Entregau/entregauApiSlice";
import FiltRows from "./Entregau/Filtrows";
import KpiCards from "./Entregau/Kpicards";
import DonutCht from "./Entregau/Donutcht";
import GerBarsX from "./Entregau/Gerbarsx";
import DtlPanel from "./Entregau/Dtlpanel";

const POLL_MS = 30000;
const DEF_KPIS = { verde: 0, ambar: 0, critico: 0 };

//Construir los filtros de consulta
const buildFilters = (gerencia, grupo, cliente) => {
  const filters = {};
  if (gerencia) {
    filters["CIMS0039.GRUPIDXX_PADRE"] = gerencia;
  }
  if (grupo) {
    filters["MDOSYYYY.USRJEFOG"] = grupo;
  }
  if (cliente) {
    filters["CLIENTE"] = cliente;
  }
  return { filters };
};

/**
 * Monitor de DOs de entrega urgente sin levante definitivo
 * @author Oscar Rosero <oscar.rosero@repremundo.com.co>
 * @since 2026-05-01
 * @param {string} graphName - nombre del contenedor en GraphicFilters
 */
const Entregau = ({ graphName }) => {
  const { control, reset, setValue } = useForm({
    mode: "onBlur",
    defaultValues: { GERENCIA: "", GRUPO: "", CLIENTE: "" },
  });
  const { GERENCIA, GRUPO, CLIENTE } = useWatch({ control });

  const [grupos, setGrupos] = useState([]);
  const [kpis, setKpis] = useState(DEF_KPIS);
  const [gerenciaBars, setGerenciaBars] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelFilter, setPanelFilter] = useState("todos");
  const [detailItems, setDetailItems] = useState([]);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const pollRef = useRef(null);

  const [getMainData] = useLazyGetEntregauDataQuery();
  const [getDetail] = useLazyGetEntregauDetailQuery();
  const { data: gerenciasData } = useGetEntregauGerenciasQuery();
  const [getGrupos] = useLazyGetEntregauGruposQuery();

  const gerencias = gerenciasData?.data ?? [];
  const total = kpis.verde + kpis.ambar + kpis.critico;

  //Traer la informacion principal
  const handleMainData = useCallback((res) => {
    const d = res.data ?? {};
    setKpis({ verde: d.verde ?? 0, ambar: d.ambar ?? 0, critico: d.critico ?? 0 });
    setGerenciaBars(d.gerencias ?? []);
    setIsLoading(false);
  }, []);

  //Enviar solicitud para traer la informacion principal
  const fetchData = useCallback(
    async (gcia, grp, cli) => {
      await getMainData(buildFilters(gcia, grp, cli))
        .unwrap()
        .then(handleMainData)
        .catch(() => setIsLoading(false));
    },
    [getMainData, handleMainData],
  );

  //Enviar solicitud para traer la informacion del detalle
  const fetchDetail = useCallback(
    async (gcia, grp, cli) => {
      setIsDetailLoading(true);
      await getDetail(buildFilters(gcia, grp, cli))
        .unwrap()
        .then((res) => setDetailItems(res.data ?? []))
        .catch(() => setDetailItems([]))
        .finally(() => setIsDetailLoading(false));
    },
    [getDetail],
  );

  //Enviar solicutud por intervalo de tiempo
  useEffect(() => {
    fetchData(GERENCIA, GRUPO, CLIENTE);
    clearInterval(pollRef.current);
    pollRef.current = setInterval(() => fetchData(GERENCIA, GRUPO, CLIENTE), POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchData, GERENCIA, GRUPO, CLIENTE]);

  //Enviar solicitud cuendo cambia un filtro
  useEffect(() => {
    setValue("GRUPO", "");
    setGrupos([]);
    if (!GERENCIA) {
      return;
    }
    getGrupos({ filters: { GRUPIDXX: GERENCIA } })
      .unwrap()
      .then((res) => setGrupos(res.data ?? []))
      .catch(() => {});
  }, [GERENCIA, getGrupos, setValue]);

  //Evento para abrir el detalle
  const handleOpenPanel = useCallback(
    (filter) => {
      setPanelFilter(filter);
      setPanelOpen(true);
      fetchDetail(GERENCIA, GRUPO, CLIENTE);
    },
    [fetchDetail, GERENCIA, GRUPO, CLIENTE],
  );

  //Lipiar los filtros
  const handleResetFilters = () => {
    reset();
    setGrupos([]);
  };

  return (
    <Box spacing={4}>
      <GraphicFilters
        containerName={graphName ?? "Monitor de entrega urgente"}
        graphicKeyName="entregau"
        clearFilters={handleResetFilters}
        onClickVerMas={handleResetFilters}
      >
        <FiltRows control={control} gerencias={gerencias} grupos={grupos} gerenciaValue={GERENCIA} />
        {isLoading ? (
          <BoxStatus variant="loading" text="Cargando monitor de entrega urgente..." />
        ) : (
          <>
            <KpiCards kpis={kpis} />
            <Box style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 20 }}>
              <DonutCht
                kpis={kpis}
                total={total}
                onSegmentClick={handleOpenPanel}
                onVerDetalle={() => handleOpenPanel("todos")}
              />
              <GerBarsX data={gerenciaBars} />
            </Box>
          </>
        )}
      </GraphicFilters>
      <DtlPanel
        isOpen={panelOpen}
        rangeFilter={panelFilter}
        items={detailItems}
        isLoading={isDetailLoading}
        onClose={() => setPanelOpen(false)}
      />
    </Box>
  );
};

export default Entregau;
